import React from 'react';
import { Box, Container, Typography, useTheme } from '@mui/material';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { blogPosts } from '../../blogData';
import Seo from '../common/Seo';

const toIsoDate = (dateString) => {
	const parsed = new Date(dateString);
	return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

const BlogPost = () => {
	const { slug } = useParams();
	const theme = useTheme();
	const post = blogPosts.find((p) => p.slug === slug);
	const publishedTime = post ? toIsoDate(post.date) : undefined;
	const canonicalPath = slug ? `/blog/${slug}` : '/blog';

	const components = {
		h2: ({ node, ...props }) => <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mt: 4, mb: 2 }} {...props} />,
		p: ({ node, ...props }) => <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }} {...props} />,
		strong: ({ node, ...props }) => <Typography component="span" sx={{ color: 'text.primary', fontWeight: 600 }} {...props} />,
		a: ({ node, children, ...props }) => <a style={{ color: theme.palette.primary.main }} {...props}>{children}</a>,
		blockquote: ({ node, ...props }) => (
			<Box
				component="blockquote"
				sx={{
					borderLeft: `4px solid ${theme.palette.primary.main}`,
					pl: 2,
					ml: 0,
					fontStyle: 'italic',
					color: 'text.secondary',
				}}
				{...props}
			/>
		),
		code({ node, inline, className, children, ...props }) {
			const match = /language-(\w+)/.exec(className || '');
			return !inline && match ? (
				<Box sx={{ my: 2, borderRadius: '0.5rem', overflow: 'hidden', backgroundColor: theme.palette.background.default }}>
					<SyntaxHighlighter style={vscDarkPlus} language={match[1]} PreTag="div" {...props}>
						{String(children).replace(/\n$/, '')}
					</SyntaxHighlighter>
				</Box>
			) : (
				<code
					className={className}
					style={{
						backgroundColor: theme.palette.background.default,
						padding: '0.1rem 0.3rem',
						borderRadius: '0.25rem',
					}}
					{...props}
				>
					{children}
				</code>
			);
		},
	};

	if (!post) {
		return (
			<>
				<Seo
					title="Post Not Found - BrokenCosmos Labs"
					description="This blog post could not be found."
					path={canonicalPath}
					noIndex
				/>
				<Box sx={{ py: { xs: 10, md: 15 }, textAlign: 'center' }}>
					<Container>
						<Typography variant="h2" component="h1">Post Not Found</Typography>
						<Typography>The blog post you're looking for doesn't exist.</Typography>
					</Container>
				</Box>
			</>
		);
	}

	const articleJsonLd = {
		'@context': 'https://schema.org',
		'@type': 'BlogPosting',
		mainEntityOfPage: `https://brokencosmos.com${canonicalPath}`,
		headline: post.title,
		description: post.excerpt,
		image: 'https://brokencosmos.com/logo512.png',
		datePublished: publishedTime,
		author: {
			'@type': 'Organization',
			name: post.author,
		},
		publisher: {
			'@type': 'Organization',
			name: 'BrokenCosmos Labs',
			logo: {
				'@type': 'ImageObject',
				url: 'https://brokencosmos.com/logo512.png',
			},
		},
	};

	return (
		<>
			<Seo
				title={`${post.title} - BrokenCosmos Labs`}
				description={post.excerpt}
				path={canonicalPath}
				type="article"
				publishedTime={publishedTime}
				modifiedTime={publishedTime}
				author={post.author}
				jsonLd={articleJsonLd}
			/>
			<Box
				sx={{
					py: { xs: 10, md: 15 },
					backgroundColor: 'background.default',
				}}
			>
				<Box
					sx={{
						maxWidth: 'md',
						mx: 'auto',
						px: { xs: 2, sm: 3, md: 4 },
					}}
				>
					<Typography variant="h1" component="h1" sx={{ mb: 2 }}>
						{post.title}
					</Typography>
					<Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
						By {post.author} on {post.date}
					</Typography>
					<ReactMarkdown components={components}>{post.content}</ReactMarkdown>
				</Box>
			</Box>
		</>
	);
};

export default BlogPost;
